/**
 * First-Win Onboarding — full-screen guided wizard shown once to brand-new
 * hackers (0 CTF solves, not dismissed). Walks them to their first real flag
 * in ~15 minutes via a dedicated starter challenge. Submission goes through
 * the normal /ctf/submit so the win is a real solve (karma + XP).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Terminal, Copy, Lightbulb, CheckCircle, ArrowRight, X, Spinner, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STEPS = ["welcome", "briefing", "mission", "submit"];

export default function FirstWinWizard() {
    const { user } = useAuth();
    const [state, setState] = useState(null);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [flag, setFlag] = useState("");
    const [busy, setBusy] = useState(false);
    const [won, setWon] = useState(null); // {points}
    const [hintOpen, setHintOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        let dead = false;
        api.get("/onboarding/first-win")
            .then((r) => { if (!dead && r.data?.show) { setState(r.data); setOpen(true); } })
            .catch(() => {});
        // Nudge card (or any other surface) can re-open the wizard for users
        // who skipped it — as long as the starter flag is still unsolved.
        const reopen = () => {
            api.get("/onboarding/first-win")
                .then((r) => { if (!dead && r.data && !r.data.solved) { setState(r.data); setStep(0); setOpen(true); } })
                .catch(() => {});
        };
        window.addEventListener("hc:open-first-win", reopen);
        return () => { dead = true; window.removeEventListener("hc:open-first-win", reopen); };
    }, [user]);

    if (!open || !state) return null;
    const ch = state.challenge;

    const dismiss = async () => {
        setOpen(false);
        try { await api.post("/onboarding/first-win/dismiss"); } catch { /* noop */ }
    };

    const copyCipher = () => {
        navigator.clipboard?.writeText(ch.cipher)
            .then(() => toast.success("Payload copied"))
            .catch(() => toast.error("Could not copy — select it manually"));
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!flag.trim() || busy) return;
        setBusy(true);
        try {
            const r = await api.post("/ctf/submit", { challenge_id: ch.challenge_id, flag: flag.trim() });
            if (r.data?.correct) {
                setWon({ points: r.data.points ?? ch.points });
                window.dispatchEvent(new CustomEvent("hc:first-win-solved"));
            } else {
                toast.error(r.data?.message || "Incorrect flag. Keep digging.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#05070d]/90 backdrop-blur-sm" data-testid="first-win-wizard">
            <div className="relative w-full max-w-2xl bg-[#0b0f19] border border-cyan-500/20 rounded-sm overflow-hidden">
                {/* header strip */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-[#0e1424]">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono text-cyan-300">
                        <Terminal size={13} weight="duotone" /> first-win // onboarding
                    </div>
                    {!won && (
                        <button onClick={dismiss} data-testid="first-win-skip" className="text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-300 flex items-center gap-1">
                            skip <X size={11} />
                        </button>
                    )}
                </div>

                <div className="p-6 sm:p-8">
                    {won ? (
                        <div className="text-center space-y-4" data-testid="first-win-success">
                            <Trophy size={44} weight="duotone" className="text-amber-300 mx-auto" />
                            <h2 className="text-2xl font-black text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>First blood drawn.</h2>
                            <p className="text-sm text-slate-400">
                                You just captured your first flag — <span className="text-emerald-300 font-mono">+{won.points} karma</span> and <span className="text-cyan-300 font-mono">+30 XP</span> are on your record. That solve is real: it counts on the leaderboard.
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                                <Link to="/ctf" onClick={() => setOpen(false)} data-testid="first-win-goto-ctf"
                                      className="text-[11px] uppercase tracking-widest font-mono bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 px-4 py-2 rounded-sm flex items-center gap-1.5">
                                    <Flag size={12} weight="duotone" /> enter the ctf arena <ArrowRight size={11} />
                                </Link>
                                <Link to="/labs" onClick={() => setOpen(false)} data-testid="first-win-goto-labs"
                                      className="text-[11px] uppercase tracking-widest font-mono border border-slate-700 text-slate-300 hover:border-cyan-500/40 px-4 py-2 rounded-sm">
                                    browse labs
                                </Link>
                                <button onClick={() => setOpen(false)} data-testid="first-win-close"
                                        className="text-[11px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-300 px-3 py-2">
                                    close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {step === 0 && (
                                <div className="space-y-4" data-testid="first-win-step-welcome">
                                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-slate-500">// welcome, operative</div>
                                    <h2 className="text-2xl sm:text-3xl font-black text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                                        Your first flag in <span className="text-cyan-300">about two minutes</span>.
                                    </h2>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        Everything here — rank, XP, certifications, recruiter visibility — starts with captured flags.
                                        We prepared a starter mission so you get your first real win right now. No tooling needed, just your brain.
                                    </p>
                                </div>
                            )}
                            {step === 1 && (
                                <div className="space-y-4" data-testid="first-win-step-briefing">
                                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-slate-500">// mission briefing</div>
                                    <h2 className="text-xl font-black text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>What's a flag?</h2>
                                    <ul className="space-y-3 text-sm text-slate-400">
                                        <li className="flex gap-2.5"><Flag size={16} weight="duotone" className="text-cyan-400 shrink-0 mt-0.5" /> A <span className="text-slate-200">flag</span> is a secret string hidden inside a challenge — find it and you've proven the skill.</li>
                                        <li className="flex gap-2.5"><Terminal size={16} weight="duotone" className="text-cyan-400 shrink-0 mt-0.5" /> Flags here always look like <code className="text-emerald-300 font-mono">hc{"{"}...{"}"}</code> — submit the whole thing, braces included.</li>
                                        <li className="flex gap-2.5"><Trophy size={16} weight="duotone" className="text-amber-300 shrink-0 mt-0.5" /> Correct flags earn <span className="text-slate-200">karma + XP</span> and push you up the leaderboard recruiters browse.</li>
                                    </ul>
                                </div>
                            )}
                            {step === 2 && (
                                <div className="space-y-4" data-testid="first-win-step-mission">
                                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-slate-500">// intercepted transmission</div>
                                    <h2 className="text-xl font-black text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{ch.title} <span className="text-amber-300 text-sm font-mono">· {ch.points} pts</span></h2>
                                    <p className="text-sm text-slate-400">We intercepted a payload meant for a new operative. It's <span className="text-slate-200">encoded, not encrypted</span> — decode it to recover your flag.</p>
                                    <div className="bg-[#05070d] border border-white/10 rounded-sm p-3 flex items-center justify-between gap-2">
                                        <code className="font-mono text-emerald-300 text-sm break-all" data-testid="first-win-cipher">{ch.cipher}</code>
                                        <button onClick={copyCipher} data-testid="first-win-copy-cipher" title="Copy payload"
                                                className="shrink-0 text-slate-500 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 p-1.5 rounded-sm">
                                            <Copy size={13} />
                                        </button>
                                    </div>
                                    <button onClick={() => setHintOpen(!hintOpen)} data-testid="first-win-hint-toggle"
                                            className="text-[10px] uppercase tracking-widest font-mono text-amber-300/80 hover:text-amber-300 flex items-center gap-1.5">
                                        <Lightbulb size={12} weight="duotone" /> {hintOpen ? "hide hint" : "need a hint?"}
                                    </button>
                                    {hintOpen && (
                                        <div className="text-xs text-slate-400 font-mono bg-amber-500/5 border border-amber-500/20 rounded-sm p-3" data-testid="first-win-hint">
                                            That character set (A-Z a-z 0-9) screams <span className="text-amber-300">base64</span>. Decode with{" "}
                                            <code className="text-emerald-300">echo '{ch.cipher}' | base64 -d</code> — or any online base64 decoder.
                                        </div>
                                    )}
                                </div>
                            )}
                            {step === 3 && (
                                <div className="space-y-4" data-testid="first-win-step-submit">
                                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-slate-500">// submit your flag</div>
                                    <h2 className="text-xl font-black text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>Claim the win.</h2>
                                    <p className="text-sm text-slate-400">Paste the decoded flag below — exactly as recovered, <code className="text-emerald-300 font-mono">hc{"{"}...{"}"}</code> included.</p>
                                    <form onSubmit={submit} className="flex gap-2">
                                        <input
                                            value={flag}
                                            onChange={(e) => setFlag(e.target.value)}
                                            placeholder="hc{...}"
                                            data-testid="first-win-flag-input"
                                            className="flex-1 bg-[#05070d] border border-slate-700 focus:border-cyan-500/50 rounded-sm px-3 py-2.5 text-sm font-mono text-slate-100 outline-none"
                                            autoFocus
                                        />
                                        <button type="submit" disabled={busy || !flag.trim()} data-testid="first-win-flag-submit"
                                                className="text-[11px] uppercase tracking-widest font-mono bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-40 px-4 py-2 rounded-sm flex items-center gap-1.5">
                                            {busy ? <Spinner size={13} className="animate-spin" /> : <CheckCircle size={13} weight="duotone" />} submit
                                        </button>
                                    </form>
                                    <button onClick={() => setStep(2)} className="text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-300">← back to the payload</button>
                                </div>
                            )}

                            {/* footer nav */}
                            <div className="flex items-center justify-between mt-8">
                                <div className="flex items-center gap-1.5">
                                    {STEPS.map((s, i) => (
                                        <span key={s} className={`h-1 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-cyan-400" : i < step ? "w-2.5 bg-cyan-400/40" : "w-2.5 bg-slate-700"}`} />
                                    ))}
                                </div>
                                {step < 3 && (
                                    <button onClick={() => setStep(step + 1)} data-testid="first-win-next"
                                            className="text-[11px] uppercase tracking-widest font-mono bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 px-4 py-2 rounded-sm flex items-center gap-1.5">
                                        {step === 0 ? "start mission" : step === 2 ? "i decoded it" : "continue"} <ArrowRight size={11} />
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
